
import React, { useEffect } from 'react'
import { editGroup } from './editGroupSlice';
import { createGroup } from './createGroupSlice';
import { Action } from 'redux';
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from 'redux-thunk';
import { useCallback, useState } from 'react';
import { IGroup, IGroupState } from '../../common/types/groupTypes';
import useNotification from '../notification/useNotification'
import { useSpinner } from '../../customHooks';
import useHandleGroupSearch from '../../pages/deviceManagement/useHandleGroupSearch';
import { groupList } from '../../pages/deviceManagement/groupSlice';
import { selector as groupApi } from '../../pages/deviceManagement/groupSlice';
import { DefaultTopLevelGroup, Selected } from "../../common/constants";
import { IGroupAlerts } from '../../common/types/alertsTypes';
import { selector as createEditGroupModalSelector, actions as modalActions } from './createEditGroupModalSlice';
import { selector as editGroupSelector, setEditGroupData } from './editGroupSlice';
import { isSuccessResponse } from '../../common/utils';
import { ICustomHook } from '../../common/types/commonTypes';

interface ICreateEditGroupResponseState {
    editGroup: IGroupState
    createGroup: IGroupState
}

export const useCreateEditGroup = (): ICustomHook => {
    const { isActive: isModalActive, isEditMode: isEdit } = useSelector(createEditGroupModalSelector);
    const { orgId: orgIdInput, parentHierarchy } = useSelector(editGroupSelector);

    const groupIdToHierarchyMap: Record<string, string> = useSelector(groupApi).groupIdToHierarchyMap;

    const displayNameDefaultValue: string = parentHierarchy ? parentHierarchy.includes('/') ? parentHierarchy.split('/').pop() : parentHierarchy : '';
    const selectedRadioDefaultValue: string = parentHierarchy ? Selected.YES : Selected.NO;
    let defaultParent: string = DefaultTopLevelGroup.ID;
    if (isEdit && parentHierarchy && parentHierarchy.includes('/')) {
        const parentId: string = parentHierarchy.split('/')[0];
        defaultParent = Object.keys(groupIdToHierarchyMap).filter(group => groupIdToHierarchyMap[group] === parentId)[0];
    }
    const checkedBox: string[] = parentHierarchy ? [defaultParent] : [];
    const dispatch: ThunkDispatch<ICreateEditGroupResponseState, undefined, Action> = useDispatch();
    const createGroupResponse = useSelector((state: ICreateEditGroupResponseState) => state.createGroup);
    const editGroupResponse = useSelector((state: ICreateEditGroupResponseState) => state.editGroup);


    const [displayName, setDisplayName] = useState<string>(displayNameDefaultValue);

    const { showSuccessNotification, showWarningNotification } = useNotification();
    const [selectedRadio, setSelectedRadio] = useState<string>(selectedRadioDefaultValue);

    const [parentOrg, setParentOrg] = useState<string>(parentHierarchy ? defaultParent : null);

    const handleDataFromTreeView = (selectedGroup: IGroupAlerts) => {
        setParentOrg(selectedGroup?.id)
    };

    useEffect(() => {
        if (isModalActive) {
            handleShow();
        }
    }, [isModalActive])

    useSpinner(createGroupResponse.status);
    useSpinner(editGroupResponse.status);

    const {
        handleGroupInputChange,
        groupSearchQuery,
        nodesToDisplay,
        setGroupSearchQuery,
    } = useHandleGroupSearch({ isEditable: isEdit });

    const handleClose = (): void => {
        dispatch(modalActions.hideModal());
        setSelectedRadio(isEdit && parentHierarchy ? Selected.YES : Selected.NO);
        if (isEdit) {
            dispatch(setEditGroupData(null));
        }
    }
    const handleShow = (): void => {
        setGroupSearchQuery('');
        setDisplayName(displayNameDefaultValue);
        setSelectedRadio(selectedRadioDefaultValue);
    };

    const handleRadioChange = useCallback((value: string) => {
        setSelectedRadio(value);
        if (value == Selected.NO) {
            setGroupSearchQuery('')
        }
    }, []);

    const showParentGroup: boolean = selectedRadio === Selected.YES;

    const handleDisplayNameInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayName(event.target.value);
    };

    const clearDisplayNameField = (): void => {
        setDisplayName('')
    }

    const getPayload = (): IGroup => {

        const createEditGroupPayload: IGroup = {};

        if (orgIdInput) {
            createEditGroupPayload.orgId = orgIdInput
        }

        if (parentOrg) {
            createEditGroupPayload.parent = parentOrg;
            createEditGroupPayload.hierarchy = `${groupIdToHierarchyMap[DefaultTopLevelGroup.ID]}/${groupIdToHierarchyMap[parentOrg]}`;
        }
        
        if (displayName) {
            createEditGroupPayload.displayName = displayName
        }
        if (selectedRadio === Selected.NO) {
            createEditGroupPayload.parent = DefaultTopLevelGroup.ID;
            createEditGroupPayload.hierarchy = groupIdToHierarchyMap[DefaultTopLevelGroup.ID];
        }

        return createEditGroupPayload;
    }

    const dispatchCreateOrUpdateGroup = (
        action,
        payload,
    ) => {
        try {
            dispatch(action(payload)).then((response) => {
                if (isSuccessResponse(response.payload)) {
                    dispatch(groupList());
                    showSuccessNotification(response.payload.message);
                } else {
                    showWarningNotification(response.payload.response.data.message);
                }
            });
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const handleAddButtonClick = () => {
        const createEditGroupPayload = getPayload();
        isEdit ? dispatchCreateOrUpdateGroup(editGroup, createEditGroupPayload) : dispatchCreateOrUpdateGroup(createGroup, createEditGroupPayload);
        dispatch(modalActions.hideModal());
        setDisplayName('');
        setGroupSearchQuery('');
        setParentOrg('');
        setSelectedRadio(Selected.NO);
        if (isEdit) {
            dispatch(setEditGroupData(null));
        }
    };

    return {
        displayName,
        checkedBox,
        isModalActive,
        handleDisplayNameInputChange,
        handleGroupInputChange,
        handleAddButtonClick,
        handleClose,
        handleShow,
        handleRadioChange,
        showParentGroup,
        selectedRadio,
        groupSearchQuery,
        nodesToDisplay,
        isEdit,
        clearDisplayNameField,
        handleDataFromTreeView,
    };
}

useCreateEditGroup.defaultProps = {
    isEdit: false,
    orgIdInput: null
}